import { useState, useEffect } from "react";
import {
    Shield,
    ShieldAlert,
    ShieldCheck,
    RefreshCw,
    Activity,
    Clock,
    Cloud,
    Network,
    Search as SearchIcon,
    AlertTriangle,
    Eye,
    Bug,
    ShieldEllipsis
} from "lucide-react";
import baseapi from "../../../services/baseapi"; // Adjust import path
import { showError } from "../../../utils/toast"; // Adjust import path

interface DefenderData {
    id: number;
    am_service_enabled: boolean | null;
    antispyware_enabled: boolean | null;
    antispyware_signature_age_days: number | null;
    antivirus_enabled: boolean | null;
    antivirus_signature_age_days: number | null;
    antivirus_signature_version: string | null;
    behavior_monitoring: boolean | null;
    cloud_protection: boolean | null;
    detected_threats: number | null;
    last_full_scan_end: string | null;
    last_full_scan_start: string | null;
    last_quick_scan_start: string | null;
    network_protection: boolean | null;
    quarantine_count: number | null;
    real_time_protection: boolean | null;
    not_supported?: boolean | null;
}

interface AssetWindowsDefenderProps {
    assetId: string | number;
    client_id: string | number;
}

export default function AssetWindowsDefender({ assetId, client_id }: AssetWindowsDefenderProps) {
    const [data, setData] = useState<DefenderData | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchDefenderData = async () => {
        setLoading(true);
        try {
            const response = await baseapi.get("/asset_windows_defender", {
                params: { query: JSON.stringify({ asset_id: assetId, client_id }) }
            });

            if (response.data.status && response.data.data && response.data.data.length > 0) {
                setData(response.data.data[0]);
            } else {
                setData(null);
            }
        } catch (error: any) {
            showError(error.message || "Failed to fetch Windows Defender data");
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (assetId) {
            fetchDefenderData();
        }
    }, [assetId]);

    // Helper to render status badges
    const StatusBadge = ({ enabled, text }: { enabled: boolean | null, text?: string }) => {
        if (enabled === true) {
            return (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                    <ShieldCheck size={12} /> {text || "Enabled"}
                </span>
            );
        }
        if (enabled === false) {
            return (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-red-500/10 text-red-400 border border-red-500/20">
                    <ShieldAlert size={12} /> {text || "Disabled"}
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-gray-500/10 text-gray-200 border border-gray-500/20">
                <Shield size={12} /> {text || "Unknown"}
            </span>
        );
    };

    // Helper to format dates
    const formatDate = (dateStr: string | null) => {
        if (!dateStr || dateStr === "Never") return "Never";

        try {
            // add Z if timezone missing → treat as UTC
            const utcString = dateStr.endsWith("Z") ? dateStr : dateStr + "Z";

            const date = new Date(utcString);
            if (isNaN(date.getTime())) return dateStr;

            return date.toLocaleString(); // converts to user's local timezone
        } catch {
            return dateStr;
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center p-12 space-y-4 bg-gray-900 rounded-xl border border-gray-800 h-full min-h-[400px]">
                <div className="w-8 h-8 border-2 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
                <p className="text-xs font-bold text-gray-200 uppercase tracking-widest">Loading Defender Stats...</p>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="flex flex-col items-center justify-center p-12 bg-gray-900 rounded-xl border border-gray-800 opacity-80 h-full min-h-[400px]">
                <Shield size={32} className="text-gray-200 mb-3" />
                <p className="text-[11px] font-bold text-gray-200 uppercase tracking-widest">No Defender data found</p>
            </div>
        );
    }

    if (data.not_supported === true) {
        return (
            <div className="flex flex-col items-center justify-center p-12 bg-gray-900 rounded-xl border border-gray-800 opacity-80 h-full min-h-[400px]">
                <AlertTriangle size={32} className="text-amber-400 mb-3" />
                <p className="text-[11px] font-bold text-gray-200 uppercase tracking-widest">Not Supported</p>
            </div>
        );
    }

    // Determine overall health context
    const isHealthy =
        data.antivirus_enabled &&
        data.real_time_protection &&
        data.am_service_enabled;

    return (
        <div className="flex flex-col text-gray-200 relative pb-6 p-2">
            {/* Header */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-4 bg-gray-800/40 p-4 rounded-xl border border-gray-700/50 shadow-lg">
                <div className="flex items-center gap-2 pr-6 border-r border-gray-700/50">
                    <div className="p-1.5 bg-orange-600 rounded mr-1">
                        <ShieldEllipsis className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-white leading-tight">Windows Defender</h2>
                        <p className="text-[10px] text-gray-200 uppercase font-semibold tracking-wider mt-0.5">Antimalware Platform</p>
                    </div>
                </div>

                <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-gray-200 uppercase tracking-wider">Health Status:</span>
                        {isHealthy ? (
                            <span className="text-xs font-bold text-emerald-400 flex items-center gap-1.5"><ShieldCheck size={14} /> Protected</span>
                        ) : (
                            <span className="text-xs font-bold text-red-400 flex items-center gap-1.5"><AlertTriangle size={14} /> Action Required</span>
                        )}
                    </div>

                    <button
                        onClick={fetchDefenderData}
                        disabled={loading}
                        className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white border border-gray-700 rounded-md transition-colors shadow-sm flex items-center justify-center disabled:opacity-50 h-8 cursor-pointer"
                        title="Refresh data"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-orange-500' : ''}`} />
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Core Protection Card */}
                <div className="bg-gray-800/30 rounded-xl border border-gray-700/50 p-4 shadow-sm flex flex-col">
                    <h3 className="text-[11px] font-black text-gray-300 uppercase tracking-widest flex items-center gap-2 mb-4 border-b border-gray-700/50 pb-2">
                        <ShieldCheck size={14} className="text-orange-500" /> Core Protection
                    </h3>
                    <div className="space-y-3.5 text-[11px]">
                        <div className="flex justify-between items-center">
                            <span className="text-gray-300">Antivirus Engine</span>
                            <StatusBadge enabled={data.antivirus_enabled} />
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-gray-300">Real-Time Protection</span>
                            <StatusBadge enabled={data.real_time_protection} />
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-gray-300">Anti-Spyware</span>
                            <StatusBadge enabled={data.antispyware_enabled} />
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-gray-700/30">
                            <span className="text-gray-300">AM Service Running</span>
                            <StatusBadge enabled={data.am_service_enabled} />
                        </div>
                    </div>
                </div>

                {/* Advanced Features Card */}
                <div className="bg-gray-800/30 rounded-xl border border-gray-700/50 p-4 shadow-sm flex flex-col">
                    <h3 className="text-[11px] font-black text-gray-300 uppercase tracking-widest flex items-center gap-2 mb-4 border-b border-gray-700/50 pb-2">
                        <Activity size={14} className="text-orange-500" /> Advanced Features
                    </h3>
                    <div className="space-y-3.5 text-[11px]">
                        <div className="flex justify-between items-center">
                            <span className="text-gray-300 flex items-center gap-1.5"><Cloud size={12} className="text-gray-200" /> Cloud Protection</span>
                            <StatusBadge enabled={data.cloud_protection} />
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-gray-300 flex items-center gap-1.5"><Network size={12} className="text-gray-200" /> Network Protection</span>
                            <StatusBadge enabled={data.network_protection} />
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-gray-300 flex items-center gap-1.5"><Eye size={12} className="text-gray-200" /> Behavior Monitoring</span>
                            <StatusBadge enabled={data.behavior_monitoring} />
                        </div>

                        <div className="flex justify-between items-center pt-2 border-t border-gray-700/30">
                            <span className="text-gray-300 flex items-center gap-1.5"><Bug size={12} className="text-red-400" /> Detected Threats</span>
                            <span className={`font-bold ${data.detected_threats && data.detected_threats > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                                {data.detected_threats || 0}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Signatures & Scans Card */}
                <div className="bg-gray-800/30 rounded-xl border border-gray-700/50 p-4 shadow-sm flex flex-col">
                    <h3 className="text-[11px] font-black text-gray-300 uppercase tracking-widest flex items-center gap-2 mb-4 border-b border-gray-700/50 pb-2">
                        <SearchIcon size={14} className="text-orange-400" /> Signatures & Scans
                    </h3>
                    <div className="space-y-3.5 text-[11px]">
                        <div className="flex justify-between items-center">
                            <span className="text-gray-300">Signature Version</span>
                            <span className="font-mono text-gray-300 bg-gray-900 px-1.5 py-0.5 rounded border border-gray-700">
                                {data.antivirus_signature_version || "-"}
                            </span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-gray-300">AV Signature Age</span>
                            <span className={`font-bold ${data.antivirus_signature_age_days && data.antivirus_signature_age_days > 7 ? 'text-red-400' : 'text-emerald-400'}`}>
                                {data.antivirus_signature_age_days !== null ? `${data.antivirus_signature_age_days} days` : "-"}
                            </span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-gray-300 flex items-center gap-1"><Clock size={10} /> Last Quick Scan</span>
                            <span className="text-gray-200 truncate max-w-[140px]" title={formatDate(data.last_quick_scan_start)}>
                                {formatDate(data.last_quick_scan_start)}
                            </span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-gray-300 flex items-center gap-1"><Clock size={10} /> Last Full Scan</span>
                            <span className="text-gray-200 truncate max-w-[140px]" title={formatDate(data.last_full_scan_start)}>
                                {formatDate(data.last_full_scan_start)}
                            </span>
                        </div>

                        <div className="flex justify-between items-center pt-2 border-t border-gray-700/30">
                            <span className="text-gray-300 flex items-center gap-1.5"><Shield size={12} className="text-amber-400" /> Quarantined Items</span>
                            <span className={`font-bold ${data.quarantine_count && data.quarantine_count > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                                {data.quarantine_count || 0}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}